import React, { Suspense, useEffect, useRef, useState } from "react";
import {
  CREST_INNER,
  CREST_OUTER,
  CREST_VIEWBOX,
} from "../BrandCrest/paths";

/** Keep in sync with `.page-loader__path` duration (+ inner delay) + brief settle. */
export const LOADER_DRAW_MS = 1280;
/** Keep in sync with `.page-loader-exit` animation. */
export const LOADER_EXIT_MS = 360;
const DRAW_DURATION_MS = 1100;
const INNER_DELAY_MS = 100;

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function CrestMark({ startedAt, drawn = false }) {
  const elapsed = startedAt != null ? performance.now() - startedAt : 0;
  const outerDelay =
    !drawn && startedAt != null
      ? `${-Math.min(elapsed, DRAW_DURATION_MS)}ms`
      : undefined;
  const innerDelay =
    !drawn && startedAt != null
      ? `${-Math.min(elapsed, DRAW_DURATION_MS + INNER_DELAY_MS) + INNER_DELAY_MS}ms`
      : undefined;

  return (
    <svg
      className={`page-loader__svg${drawn ? " page-loader__svg--drawn" : ""}`}
      viewBox={CREST_VIEWBOX}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        className={`page-loader__path page-loader__path--outer${drawn ? " page-loader__path--drawn" : ""}`}
        fillRule="evenodd"
        pathLength="1"
        style={outerDelay != null ? { animationDelay: outerDelay } : undefined}
        d={CREST_OUTER}
      />
      <path
        className={`page-loader__path page-loader__path--inner${drawn ? " page-loader__path--drawn" : ""}`}
        fillRule="evenodd"
        pathLength="1"
        style={innerDelay != null ? { animationDelay: innerDelay } : undefined}
        d={CREST_INNER}
      />
    </svg>
  );
}

/** Full-screen brand crest redraw (route transitions). */
export function PageLoader({ startedAt }) {
  return (
    <div
      className="page-loader min-h-screen flex items-center justify-center bg-[#0f1115]"
      role="status"
      aria-label="Loading"
    >
      <CrestMark startedAt={startedAt} />
    </div>
  );
}

/**
 * In-layout wait state after the route draw — filled crest pulse, no second redraw.
 * Use this instead of the old lime spin circles.
 */
export function CrestWait({ className = "flex h-64 items-center justify-center" }) {
  return (
    <div className={className} role="status" aria-label="Loading">
      <CrestMark drawn />
    </div>
  );
}

/** Soft enter for page content (also used after CrestWait clears). */
export function PageEnter({ children }) {
  return <div className="page-reveal">{children}</div>;
}

function RevealAfterDraw({ children, startedAt, minMs }) {
  const [phase, setPhase] = useState(() =>
    performance.now() - startedAt >= minMs ? "done" : "hold"
  );

  useEffect(() => {
    if (phase !== "hold") return undefined;
    const wait = Math.max(0, minMs - (performance.now() - startedAt));
    const timer = setTimeout(() => {
      setPhase(prefersReducedMotion() ? "done" : "crossfade");
    }, wait);
    return () => clearTimeout(timer);
  }, [phase, startedAt, minMs]);

  useEffect(() => {
    if (phase !== "crossfade") return undefined;
    const timer = setTimeout(() => setPhase("done"), LOADER_EXIT_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  if (phase === "hold") {
    return <PageLoader startedAt={startedAt} />;
  }

  return (
    <div className="page-transition">
      <div className={phase === "done" ? undefined : "page-reveal"}>{children}</div>
      {phase === "crossfade" && (
        <div className="page-loader-exit" aria-hidden="true">
          <PageLoader startedAt={startedAt} />
        </div>
      )}
    </div>
  );
}

/** Suspense wrapper that waits for one full crest redraw before revealing. */
export function LazyRoute({ children, minMs = LOADER_DRAW_MS }) {
  const startedAt = useRef(performance.now());

  return (
    <Suspense fallback={<PageLoader startedAt={startedAt.current} />}>
      <RevealAfterDraw startedAt={startedAt.current} minMs={minMs}>
        {children}
      </RevealAfterDraw>
    </Suspense>
  );
}

export default PageLoader;
